// @ts-nocheck
import constants from "@store/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import TextField from "@store/MobiusOverrides/TextField/TextField";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Search from "@toolkit/Library/Components/Icons/Search";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { connect } from "react-redux";
import { css, ThemeProps } from "styled-components";

const enum fields {
    placeholder = "placeholder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.placeholder]: string;
    };
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: BlogSearchInputStyles;
    placeholder?: string;
}

const mapStateToProps = (state: Toolkit.Store.ApplicationState) => {
    const location = getLocation(state);
    const parsedQueryString = parseQueryString<{ query?: string }>(location.search);
    const currentPage = getCurrentPage(state);
    return {
        searchPageUrl: getPageLinkByPageType(state, constants.pageType.blogList)?.url,
        blogHomePageUrl: getPageLinkByPageType(state, constants.pageType.blogHome)?.url,
        initialQuery: parsedQueryString.query || "",
        isSearchPage: currentPage.type === constants.pageType.blogList,
    };
};

type Props = OwnProps & State & ThemeProps<BaseTheme> & HasHistory & ReturnType<typeof mapStateToProps>;

interface State {
    query: string;
    isLoading: boolean;
}

export interface BlogSearchInputStyles {
    wrapper?: InjectableCss;
    input?: TextFieldPresentationProps;
    clearResultsLink?: LinkPresentationProps;
}

export const searchInputStyles: BlogSearchInputStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${pxToRem(10)};
            width: 100%;
        `,
    },
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
                width: 100%;
            `,
        },
    },
    clearResultsLink: {
        icon: {
            iconProps: {
                src: "XCircle",
                color: "secondary",
            },
            position: "left",
        },
        typographyProps: {
            css: css`
                padding-left: ${pxToRem(5)};
            `,
        },
    },
};

const styles = searchInputStyles;

const ENTER_KEY = 13;

export const BlogSearch: React.FC<Props> = ({
    id,
    fields: { placeholder },
    searchPageUrl,
    blogHomePageUrl,
    inputRef,
    onBeforeGoToUrl,
    initialQuery,
    isSearchPage,
}: Props) => {
    const history = useHistory();
    const [query, setQuery] = React.useState(initialQuery);

    const searchId = `blogSearch-${id}`;

    const doSearch = () => {
        if (query.trim() === "") {
            return;
        }
        goToUrl(`${searchPageUrl || "/blog/search"}?query=${query}`, false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setQuery(newQuery);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === ENTER_KEY) {
            doSearch();
        }
    };

    const goToUrl = (url: string, track = true) => {
        onBeforeGoToUrl && onBeforeGoToUrl();
        history.push(url);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                {...styles.input}
                ref={inputRef}
                iconClickableProps={{ onClick: doSearch }}
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || translate("Search")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="blogSearchInputTextField"
            />
            {isSearchPage && (
                <Link href={blogHomePageUrl} {...styles.clearResultsLink}>
                    {translate("Clear search")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps)(BlogSearch)),
    definition: {
        icon: "search",
        group: constants.widgetGroup.blogPage as any,
        displayName: "Blog Search Input",
        allowedContexts: [constants.pageType.blogHome, constants.pageType.blogList, constants.pageType.blogPost] as any,
        fieldDefinitions: [
            {
                name: fields.placeholder,
                editorTemplate: "TextField",
                displayName: "Placeholder text in the searchbox",
                fieldType: "Translatable",
                defaultValue: "Search Our Blog",
            },
        ],
    },
};

export default widgetModule;
